import React, { useContext, useRef } from 'react';
import {
  SnackbarContext,
  AbortableFetch,
  useEventCallback,
  DetailContext,
  FormTextArea,
  FormDateField,
  abortableFetch,
  UserContext,
  PromptContext,
  usePrompts,
} from '@eas/common-web';
import { unstable_batchedUpdates } from 'react-dom';
import { EvidenceAPI, Permission, Messages } from '../../../enums';
import { HelpLabel } from '../../../components/help/help-label';

/**
 * Api call
 *
 * @param id
 * @param values
 */
export function callApi(
  id: string,
  values: { note?: string; validTo?: string }
) {
  return abortableFetch(`${EvidenceAPI.IRZ_FACILITIES}/${id}/active`, {
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    method: 'PUT',
    body: JSON.stringify(values),
  });
}

/**
 * Obnovení platnosti provozovny IRZ
 */
const PROMPT_KEY = 'ACTIVATE';

export function useActivateDialog() {
  /**
   * Context stuff.
   */
  const { onPersisted, source } = useContext(DetailContext);
  const { showSnackbar } = useContext(SnackbarContext);
  const { testPrompt } = useContext(PromptContext);
  const { hasPermission } = useContext(UserContext);

  /**
   * Fetch ref.
   */
  const fetch = useRef<AbortableFetch | null>(null);

  /**
   * Dialogs.
   */
  usePrompts([
    {
      key: PROMPT_KEY,
      dialogTitle: 'Obnovení platnosti provozovny IRZ',
      dialogText: 'Skutečně chcete obnovit platnost provozovny IRZ?',
      FormFields: function Body() {
        return (
          <>
            <FormTextArea
              name="note"
              label={
                <HelpLabel
                  label="Poznámka"
                  code="IRZ_FACILITY_ACTIVATE_DIALOG_NOTE"
                />
              }
            />
            <FormDateField
              name="validFrom"
              label={
                <HelpLabel
                  label="Datum"
                  code="IRZ_FACILITY_ACTIVATE_DIALOG_VALID_FROM"
                />
              }
            />
          </>
        );
      },
      dialogWidth: 600,
    },
  ]);

  const handleActivate = useEventCallback(async () => {
    testPrompt({
      key: PROMPT_KEY,
      submitCallback: async (values: { note?: string; validFrom?: string }) => {
        try {
          source.setLoading(true);

          if (fetch.current !== null) {
            fetch.current.abort();
          }
          fetch.current = callApi(source.data!.id, values);

          await fetch.current.raw();

          unstable_batchedUpdates(() => {
            showSnackbar(...Messages.IrzFacility.ACTIVATE.SUCCESS);
            source.setLoading(false);
          });

          onPersisted(source.data!.id);
          await source.refresh();
        } catch (err) {
          source.setLoading(false);

          if (err.name !== 'AbortError') {
            showSnackbar(...Messages.IrzFacility.ACTIVATE.ERROR);
            throw err;
          }
          return undefined;
        }
      },
    });
  });

  const showActivateButton =
    hasPermission(Permission.IrzFacility.IRZ_REVALIDATE) &&
    !source.data?.active;

  return {
    handleActivate,
    showActivateButton,
  };
}
